import { useState } from "react";
import Select from "react-select";
import { useProductos } from "@/hooks/useProductos";
import { toast } from "react-toastify";

const pad = (n) => String(n).padStart(2, "0");
const todayYMD = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Componente inline para cada cargo
function CargoItem({ productos, value, onChange }) {
    const handleSelectProducto = (selectedOption) => {
        if (!selectedOption) {
            onChange({
                ...value,
                producto_id: "",
                descripcion: "",
                importe: "",
            });
            return;
        }

        const p = productos.find((prod) => prod.id === selectedOption.value);
        onChange({
            ...value,
            producto_id: selectedOption.value,
            descripcion: p ? (p.descripcion || p.nombre || "") : "",
            importe: p?.precio != null ? Number(p.precio).toFixed(2) : "",
        });
    };

    // Convertir productos a formato react-select
    const productOptions = productos.map((p) => ({
        value: p.id,
        label: `${p.nombre}${p.precio != null ? ` — $${Number(p.precio).toLocaleString("es-MX", { minimumFractionDigits: 2 })}` : ""}`,
    }));

    // Encontrar la opción seleccionada actual
    const selectedOption = productOptions.find(
        (opt) => opt.value === Number(value.producto_id)
    ) || null;

    // Estilos personalizados para react-select
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
            boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
            "&:hover": {
                borderColor: "#9ca3af",
            },
            padding: "2px",
        }),
        menu: (base) => ({
            ...base,
            zIndex: 50,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected
                ? "#3b82f6"
                : state.isFocused
                    ? "#e0e7ff"
                    : "white",
            color: state.isSelected ? "white" : "#1e293b",
            "&:active": {
                backgroundColor: "#2563eb",
            },
        }),
    };

    return (
        <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
                {/* Servicio */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Servicio / concepto
                    </label>
                    <Select
                        options={productOptions}
                        value={selectedOption}
                        onChange={handleSelectProducto}
                        placeholder="— Selecciona —"
                        isClearable
                        isSearchable
                        noOptionsMessage={() => "No se encontraron productos"}
                        styles={customStyles}
                        className="text-sm"
                    />
                </div>

                {/* Importe */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Importe
                    </label>
                    <input
                        type="text"
                        inputMode="decimal"
                        className="w-full rounded border px-3 py-2"
                        placeholder="0.00"
                        value={value.importe || ""}
                        onChange={(e) => {
                            const v = e.target.value.replace(/[^\d.]/g, "");
                            const parts = v.split(".");
                            onChange({ ...value, importe: parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : v });
                        }}
                        onBlur={() => {
                            if (value.importe) onChange({ ...value, importe: Number(value.importe).toFixed(2) });
                        }}
                    />
                </div>
            </div>

            {/* Descripción */}
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    Descripción
                </label>
                <input
                    className="w-full rounded border px-3 py-2"
                    placeholder="Descripción del cargo"
                    value={value.descripcion || ""}
                    onChange={(e) => onChange({ ...value, descripcion: e.target.value })}
                />
            </div>

            {/* Comentario */}
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                    Comentario (opcional)
                </label>
                <input
                    className="w-full rounded border px-3 py-2"
                    placeholder="Comentario visible"
                    value={value.comentario || ""}
                    onChange={(e) => onChange({ ...value, comentario: e.target.value })}
                />
            </div>
        </div>
    );
}

export default function CargoRecurrente({ empresaId, onCreate }) {
    const { productos, loading, err } = useProductos();

    const [modo, setModo] = useState("single");
    const [items, setItems] = useState([
        { producto_id: "", descripcion: "", importe: "", comentario: "" }
    ]);

    const [tipo, setTipo] = useState("mensual");
    const [fechaInicio, setFechaInicio] = useState(todayYMD());
    const [tipoOcurrencia, setTipoOcurrencia] = useState("infinito");
    const [numOcurrencias, setNumOcurrencias] = useState(1);
    const [ejecutarInmediatamente, setEjecutarInmediatamente] = useState(true);

    const addItem = () => setItems([...items, { producto_id: "", descripcion: "", importe: "", comentario: "" }]);
    const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
    const updateItem = (i, value) => setItems(items.map((it, idx) => idx === i ? value : it));

    const handleGuardar = async () => {
        try {
            if (!fechaInicio) throw new Error("Selecciona la fecha de inicio");
            if (tipoOcurrencia === "especifico" && numOcurrencias < 1) {
                throw new Error("El número de ocurrencias debe ser al menos 1");
            }

            const lista = modo === "single" ? [items[0]] : items;
            lista.forEach((it, i) => {
                if (!it.producto_id) throw new Error(`Item ${i + 1}: sin producto`);
                if (!it.importe || Number(it.importe) <= 0) throw new Error(`Item ${i + 1}: sin importe válido`);
            });

            const payload = {
                empresa_id: Number(empresaId),
                cargos: lista.map((it) => ({
                    producto_id: Number(it.producto_id),
                    descripcion: it.descripcion || "",
                    importe: Number(it.importe),
                    comentario: it.comentario || null,
                })),
                recurrence: {
                    tipo,
                    fecha_inicio: fechaInicio,
                    tipo_ocurrencia: tipoOcurrencia,
                    num_ocurrencias: tipoOcurrencia === "especifico" ? Number(numOcurrencias) : null,
                    ejecutar_inmediatamente: ejecutarInmediatamente,
                },
            };

            console.log('📤 Payload antes de enviar:', JSON.stringify(payload, null, 2));

            await onCreate(payload);
            toast.success("Cargo recurrente guardado");

            // Reset
            setItems([{ producto_id: "", descripcion: "", importe: "", comentario: "" }]);
            setTipo("mensual");
            setFechaInicio(todayYMD());
            setTipoOcurrencia("infinito");
            setNumOcurrencias(1);
            setEjecutarInmediatamente(true);
        } catch (err) {
            console.error('❌ Error en handleGuardar:', err);
            toast.error(err.message || "Error al guardar");
        }
    };

    if (loading) return <div className="text-sm text-slate-500">Cargando productos...</div>;
    if (err) return <div className="text-sm text-rose-600">Error: {err}</div>;

    return (
        <div className="space-y-4">
            {/* Modo */}
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Modo:</label>
                <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            value="single"
                            checked={modo === "single"}
                            onChange={(e) => setModo(e.target.value)}
                        />
                        <span className="text-sm text-slate-700">Único</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            value="multi"
                            checked={modo === "multi"}
                            onChange={(e) => setModo(e.target.value)}
                        />
                        <span className="text-sm text-slate-700">Múltiples</span>
                    </label>
                </div>
            </div>

            {/* Cargos */}
            {modo === "single" ? (
                <CargoItem
                    productos={productos}
                    value={items[0]}
                    onChange={(v) => updateItem(0, v)}
                />
            ) : (
                <div className="space-y-3">
                    {items.map((it, i) => (
                        <div key={i} className="border rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-slate-600">Cargo #{i + 1}</span>
                                {items.length > 1 && (
                                    <button
                                        type="button"
                                        className="text-xs text-rose-600 hover:text-rose-800"
                                        onClick={() => removeItem(i)}
                                    >
                                        Quitar
                                    </button>
                                )}
                            </div>
                            <CargoItem
                                productos={productos}
                                value={it}
                                onChange={(v) => updateItem(i, v)}
                            />
                        </div>
                    ))}
                    <button
                        type="button"
                        className="w-full py-2 text-sm border-2 border-dashed rounded hover:bg-slate-50"
                        onClick={addItem}
                    >
                        + Agregar otro cargo
                    </button>
                </div>
            )}

            {/* Configuración de recurrencia */}
            <div className="border-t pt-4 space-y-4">
                <h3 className="text-sm font-semibold text-slate-700">Configuración de recurrencia</h3>

                {/* Tipo */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de recurrencia:</label>
                    <div className="flex flex-wrap gap-2">
                        {["diario", "semanal", "mensual", "anual"].map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTipo(t)}
                                className={`px-4 py-2 text-sm font-medium rounded transition-colors ${tipo === t
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50"
                                    }`}
                            >
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Checkbox de ejecución inmediata */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={ejecutarInmediatamente}
                            onChange={(e) => setEjecutarInmediatamente(e.target.checked)}
                            className="mt-0.5 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                            <span className="block text-sm font-medium text-slate-700">
                                Ejecutar primer cargo inmediatamente
                            </span>
                            <span className="block text-xs text-slate-600 mt-1">
                                {ejecutarInmediatamente
                                    ? "✓ El primer cargo se ejecutará HOY, y los siguientes según la recurrencia desde hoy"
                                    : "○ El primer cargo se ejecutará el día configurado abajo, y los siguientes según la recurrencia desde esa fecha"}
                            </span>
                        </div>
                    </label>
                </div>

                {/* Fecha de inicio - Solo visible si NO es inmediato */}
                {!ejecutarInmediatamente && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Fecha del primer cargo
                        </label>
                        <input
                            type="date"
                            className="w-full rounded border-blue-300 px-3 py-2 focus:ring-2 focus:ring-blue-500"
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.target.value)}
                        />
                        <p className="text-xs text-slate-600 mt-2">
                            El primer cargo se ejecutará en esta fecha a las <strong>00:00:00</strong>
                        </p>
                    </div>
                )}

                {/* Si es inmediato, mostrar info de que fecha_inicio se ignora */}
                {ejecutarInmediatamente && (
                    <div className="text-xs text-slate-500 italic">
                        Nota: Como elegiste ejecutar inmediatamente, el primer cargo será HOY y no se usa una fecha específica.
                    </div>
                )}

                {/* Ocurrencias */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Número de ocurrencias:</label>
                    <div className="space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="occurrenceType"
                                value="infinito"
                                checked={tipoOcurrencia === "infinito"}
                                onChange={(e) => setTipoOcurrencia(e.target.value)}
                                className="w-4 h-4 text-blue-600"
                            />
                            <span className="text-sm text-slate-700">Infinito</span>
                        </label>

                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="occurrenceType"
                                    value="especifico"
                                    checked={tipoOcurrencia === "especifico"}
                                    onChange={(e) => setTipoOcurrencia(e.target.value)}
                                    className="w-4 h-4 text-blue-600"
                                />
                                <span className="text-sm text-slate-700">Específico:</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={numOcurrencias}
                                onChange={(e) => setNumOcurrencias(Math.max(1, parseInt(e.target.value) || 1))}
                                disabled={tipoOcurrencia !== "especifico"}
                                className="w-24 rounded border px-3 py-1 text-sm disabled:bg-slate-100 disabled:text-slate-400"
                            />
                            <span className="text-xs text-slate-500">
                                {tipo === "diario" && "día(s)"}
                                {tipo === "semanal" && "semana(s)"}
                                {tipo === "mensual" && "mes(es)"}
                                {tipo === "anual" && "año(s)"}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Info dinámica según configuración */}
                <div className="p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600 space-y-1">
                    <p><strong> Resumen de tu configuración:</strong></p>

                    {ejecutarInmediatamente ? (
                        <>
                            <p>• <strong>Primer cargo:</strong> HOY a las 00:00:00</p>
                            {tipo === "diario" && <p>• <strong>Siguientes:</strong> Cada día desde hoy</p>}
                            {tipo === "semanal" && <p>• <strong>Siguientes:</strong> Cada semana desde hoy</p>}
                            {tipo === "mensual" && <p>• <strong>Siguientes:</strong> El día 1 de cada mes</p>}
                            {tipo === "anual" && <p>• <strong>Siguientes:</strong> Cada año desde hoy</p>}
                        </>
                    ) : (
                        <>
                            <p>• <strong>Primer cargo:</strong> {fechaInicio} a las 00:00:00</p>
                            {tipo === "diario" && <p>• <strong>Siguientes:</strong> Cada día desde el {fechaInicio}</p>}
                            {tipo === "semanal" && <p>• <strong>Siguientes:</strong> Cada semana desde el {fechaInicio}</p>}
                            {tipo === "mensual" && <p>• <strong>Siguientes:</strong> El día 1 de cada mes (desde {fechaInicio})</p>}
                            {tipo === "anual" && <p>• <strong>Siguientes:</strong> Cada año desde el {fechaInicio}</p>}
                        </>
                    )}

                    {tipoOcurrencia === "especifico" && (
                        <p>• <strong>Total de cargos:</strong> {numOcurrencias}</p>
                    )}
                    {tipoOcurrencia === "infinito" && (
                        <p>• <strong>Total de cargos:</strong> Infinito (hasta que lo desactives)</p>
                    )}
                </div>
            </div>

            {/* Botón */}
            <div className="flex justify-end">
                <button
                    type="button"
                    className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    onClick={handleGuardar}
                    disabled={loading}
                >
                    Guardar cargo recurrente
                </button>
            </div>

            {modo === "multi" && (
                <div className="text-[11px] text-slate-500">
                    En modo "Múltiples", todos los cargos comparten la misma recurrencia.
                </div>
            )}
        </div>
    );
}